import { ApolloError, useMutation } from '@apollo/client'
import { useParams } from 'react-router-dom'
import quotesListQuery from '@Builder/QuotesHomePage/QuotesTabs/query'
import orderDetailsQuery from '@Orders/OrderDetailsPage/Queries/orderDetailsQuery'
import ordersListQuery from '@Orders/AllOrdersPage/query'
import {
    PAGINATION_LIMIT,
    DEFAULT_PAGE_OFFSET,
    QUOTE_LIST_SORT_KEY,
} from '@Builder/QuotesHomePage/logic'

import {
    MoveOrderTeamDocument,
    MoveOrderTeamMutation,
    QuoteDetailsDocument,
} from '~/gql/graphql'

import moveQuoteTeamMutation from './moveQuoteTeamMutation'
import { MoveQuoteTeam } from './__generated__/MoveQuoteTeam'

interface MoveToTeamsType {
    variables: {
        id?: number
        userId?: number
        platformOrderId?: number
        erpSaleOrderId?: number
        quoteId?: string
        teamId?: string
    }
    refetchQueries: any
    onCompleted: (data: MoveQuoteTeam | MoveOrderTeamMutation) => void
    onError: (error: ApolloError) => void
}

export const updateMutationVariablesWithOrderId = (
    orderId: string,
    variables: Record<string, unknown>,
) => {
    const id: number = parseInt(orderId.split(':')[1])
    const isPlatformOrder = orderId.includes('platform:')

    if (isPlatformOrder) {
        variables['platformOrderId'] = id
    } else {
        variables['erpSaleOrderId'] = id
    }
}

export const useTeamsMoveMutation = (
    id: string,
    userId: number,
    onCompleted: (data: MoveQuoteTeam | MoveOrderTeamMutation) => void,
    onError: (error: ApolloError) => void,
) => {
    const params = useParams<{
        page?: string
        id?: string
        orderName?: string
    }>()

    const page = Number(params.page)
    const quoteId = params?.id
    const orderName = params?.orderName
    const isOrder = id.includes(':')
    const upcomingPageOffset = page
        ? PAGINATION_LIMIT * (page - 1)
        : DEFAULT_PAGE_OFFSET

    const mutationOptions: MoveToTeamsType = {
        variables: {
            userId,
        },
        refetchQueries: [
            {
                query: isOrder ? ordersListQuery : quotesListQuery,
                variables: {
                    limit: PAGINATION_LIMIT,
                    offset: upcomingPageOffset,
                    sort: QUOTE_LIST_SORT_KEY,
                },
            },
        ],
        onCompleted,
        onError,
    }

    if (quoteId) {
        mutationOptions.refetchQueries.push({
            query: QuoteDetailsDocument,
            variables: {
                id: quoteId,
            },
        })
    }
    if (orderName) {
        mutationOptions.refetchQueries.push({
            query: orderDetailsQuery,
            variables: {
                name: orderName,
            },
        })
    }

    if (isOrder) {
        updateMutationVariablesWithOrderId(id, mutationOptions.variables)
    } else {
        mutationOptions.variables['quoteId'] = id
    }

    const mutation = isOrder ? MoveOrderTeamDocument : moveQuoteTeamMutation

    return useMutation(mutation, mutationOptions)
}
